import { isFullDayLeave } from "@/requests/leaves/lib/isFullDayLeave"
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Filter, User, Files, File, FileText, ClipboardList, Watch, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "react-toastify";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';
import { getAllLeaveRequests, type LeaveRequest } from '@/lib/collections/requestsLeave/rrhh/getAllLeaveRequests';
import { isLeaveApproved, isLeaveRejected } from '@/requests/leaves/config/leaveApprovals';
import { formatDuration } from '@/requests/leaves/lib/calculateLeaveDuration';
import { updateLeaveRequestByRRHH } from '@/lib/collections/requestsLeave/rrhh/updateLeaveRequestsByRrhh';
import { fetchUsersWithProfiles, type CombinedUser } from '@/lib/manager realtime/userCache';
import { getSubordinateUserIds } from '@/lib/collections/supervisor/getSubordinateIds';


type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type ApprovalStatus = 'pending' | 'approved' | 'rejected';

const ITEMS_PER_PAGE = 6;

const STATUS_CONFIG: Record<ApprovalStatus, {
    label: string;
    variant: 'default' | 'secondary' | 'destructive';
    icon: any;
    className?: string;
}> = {
    pending: {
        label: 'Pendiente',
        variant: 'secondary',
        className: 'bg-yellow-600 dark:bg-yellow-800 text-white dark:text-white',
        icon: Clock,
    },
    approved: {
        label: 'Aprobado',
        variant: 'default',
        icon: CheckCircle2,
        className: 'bg-green-500 hover:bg-green-600 text-white dark:bg-green-600 dark:text-white',
    },
    rejected: {
        label: 'Rechazado',
        variant: 'destructive',
        icon: XCircle,
    },
};


const getOverallStatus = (request: LeaveRequest): ApprovalStatus => {
    if (isLeaveRejected(
        request.statusByRrhh,
        request.statusBySupervisor,
        request.statusByManager
    )) {
        return 'rejected';
    }

    if (isLeaveApproved(
        request.statusByRrhh,
        request.statusBySupervisor,
        request.statusByManager
    )) {
        return 'approved';
    }

    return 'pending';
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};



const StatusBadge = ({ status }: { status: ApprovalStatus }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;

    return (
        <Badge
            variant={config.variant}
            className={`flex items-center gap-1 w-fit ${config.className || ''}`}
        >
            <Icon className="w-3 h-3" />
            {config.label}
        </Badge>
    );
};

// const ApprovalStep = ({
//     status,
//     label,
//     showLine = true,
//     stepNumber
// }: {
//     status: string;
//     label: string;
//     showLine?: boolean;
//     stepNumber: number;
// }) => {
//     const getStepColor = () => {
//         if (status === 'approved') return 'bg-green-500';
//         if (status === 'rejected') return 'bg-red-500';
//         return 'bg-gray-300';
//     };

//     const getStepIcon = () => {
//         if (status === 'approved') return <CheckCircle2 className="w-4 h-4 text-white" />;
//         if (status === 'rejected') return <XCircle className="w-4 h-4 text-white" />;
//         return <span className="text-xs text-gray-600">{stepNumber}</span>;
//     };

//     return (
//         <>
//             <div className="flex items-center gap-1.5 min-w-0">
//                 <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getStepColor()}`}>
//                     {getStepIcon()}
//                 </div>
//                 <span className="text-xs font-medium whitespace-nowrap">{label}</span>
//             </div>
//             {showLine && (
//                 <div className={`h-0.5 w-3 sm:w-4 shrink-0 ${status === 'approved' ? 'bg-green-500' : 'bg-gray-300'}`} />
//             )}
//         </>
//     );
// };

// const ApprovalSteps = ({ request }: { request: LeaveRequest }) => {
//     const config = getApprovalConfig();
//     const requiredApprovals = config.requiredApprovals;

//     const approvalData: Record<string, { status: string; label: string }> = {
//         rrhh: { status: request.statusByRrhh, label: 'RRHH' },
//         supervisor: { status: request.statusBySupervisor || 'pending', label: 'Supervisor' },
//         manager: { status: request.statusByManager || 'pending', label: 'Gerencia' }
//     };

//     return (
//         <div className="flex items-center gap-1.5 sm:gap-2 mt-3">
//             {requiredApprovals.map((role, index) => {
//                 const data = approvalData[role];
//                 const isLast = index === requiredApprovals.length - 1;

//                 return (
//                     <ApprovalStep
//                         key={role}
//                         status={data.status}
//                         label={data.label}
//                         showLine={!isLast}
//                         stepNumber={index + 1}
//                     />
//                 );
//             })}
//         </div>
//     );
// };

const RejectionNotice = ({ reason }: { reason: string }) => {
    return (
        <div className=" border border-red-200 rounded-md p-3">
            <p className="text-xs font-medium text-red-800 dark:text-red-500 mb-1">Motivo del rechazo:</p>
            <p className="text-xs text-red-300 dark:text-gray-400">{reason}</p>
        </div>
    );
};

const EmptyState = () => {
    return (
        <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
                <User className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No hay solicitudes</h3>
                <p className="text-muted-foreground text-center px-4">
                    No se encontraron solicitudes con los filtros aplicados
                </p>
            </CardContent>
        </Card>
    );
};


const Pagination = ({
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    total,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    total: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <p className="text-sm text-muted-foreground">
                Mostrando {startIndex + 1}-{endIndex} de {total}
            </p>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center px-3 text-sm font-medium">
                    {currentPage} / {totalPages}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};


const RRHHLeaveRequestsView = () => {
    const { user, userRoles } = useAuth();

    const [requests, setRequests] = useState<LeaveRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [users, setUsers] = useState<CombinedUser[]>([]);
    const [_loadingUsers, setLoadingUsers] = useState(true);

    const [subordinateIds, setSubordinateIds] = useState<string[]>([]);
    const [loadingSubordinates, setLoadingSubordinates] = useState(false);

    const [userFilter, setUserFilter] = useState<string>('all');

    // Dialog states
    const [detailsDialog, setDetailsDialog] = useState<{
        open: boolean;
        request: LeaveRequest | null;
    }>({ open: false, request: null });

    const [approvalDialog, setApprovalDialog] = useState<{
        open: boolean;
        request: LeaveRequest | null;
        action: 'approve' | 'reject' | null;
    }>({ open: false, request: null, action: null });

    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const isSupervisorOnly = userRoles.isSupervisor && !userRoles.isRRHH && !userRoles.isAdmin;

        // RRHH/Admin cargan directo, supervisor espera sus IDs
        if (!isSupervisorOnly || subordinateIds.length > 0) {
            loadRequests();
        }
    }, [subordinateIds, userRoles]);

    useEffect(() => {
        if (userRoles.isSupervisor && user?.$id) {
            setLoadingSubordinates(true);
            getSubordinateUserIds(user.$id)
                .then(setSubordinateIds)
                .finally(() => setLoadingSubordinates(false));
        }
    }, [userRoles.isSupervisor, user?.$id]);

    const loadRequests = async () => {
        const isSupervisorOnly = userRoles.isSupervisor && !userRoles.isRRHH && !userRoles.isAdmin;

        // Si es supervisor y aún no cargaron sus subordinados, esperar
        if (isSupervisorOnly && subordinateIds.length === 0) return;

        setLoading(true);
        const result = await getAllLeaveRequests();

        if (result.success && result.data) {
            // Si es supervisor, filtrar en frontend. Si no, mostrar todo
            const data = isSupervisorOnly
                ? result.data.filter(r => subordinateIds.includes(r.userId))
                : result.data;

            setRequests(data);
        } else {
            toast.error('Error al cargar las solicitudes');
        }
        setLoading(false);
    };

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            const usersData = await fetchUsersWithProfiles();
            setUsers(usersData);
        } catch (err) {
            console.error('Error loading users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const getUserName = (userId: string) => {
        const user = users.find(u => u.id === userId);
        return user?.name || 'Usuario desconocido';
    };

    const usersWithRequests = Array.from(
        new Map(
            requests.map(r => [r.userId, getUserName(r.userId)])
        ).entries()
    ).sort((a, b) => a[1].localeCompare(b[1]));


    const handleViewDetails = (request: LeaveRequest) => {
        setDetailsDialog({ open: true, request });
    };

    const handleApprove = (request: LeaveRequest) => {
        setDetailsDialog({ open: false, request: null });
        setApprovalDialog({ open: true, request, action: 'approve' });
    };

    const handleReject = (request: LeaveRequest) => {
        setDetailsDialog({ open: false, request: null });
        setApprovalDialog({ open: true, request, action: 'reject' });
        setRejectionReason('');
    };

    const confirmApproval = async () => {
        if (!approvalDialog.request || !user?.$id) return;

        if (approvalDialog.action === 'reject' && !rejectionReason.trim()) {
            toast.error('Debes proporcionar un motivo de rechazo');
            return;
        }

        setProcessing(true);

        try {
            const result = await updateLeaveRequestByRRHH({
                requestId: approvalDialog.request.$id,
                action: approvalDialog.action!,
                rrhhUserId: user.$id,
                rejectionReason: rejectionReason.trim() || undefined,
                leaveUserId: approvalDialog.request.userId,
                leaveTitle: approvalDialog.request.title,

                startDate: approvalDialog.request.startDate,
                endDate: approvalDialog.request.endDate,
                allDay: isFullDayLeave(approvalDialog.request),
                totalDuration: approvalDialog.request.totalDuration,
            });
            if (result.success) {
                toast.success(result.message);
                await loadRequests();
                setApprovalDialog({ open: false, request: null, action: null });
                setRejectionReason('');
            } else {
                toast.error(result.error || 'Error al procesar la solicitud');
            }
        } catch (error) {
            toast.error('Error inesperado al procesar la solicitud');
            console.error(error);
        } finally {
            setProcessing(false);
        }
    };

    // Filtering and pagination
    const filteredRequests = requests.filter(request => {
        const overallStatus = getOverallStatus(request);
        const matchesStatus = statusFilter === 'all' || overallStatus === statusFilter;
        const matchesUser = userFilter === 'all' || request.userId === userFilter;
        const matchesSearch =
            searchTerm === '' ||
            request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (request.comment?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            getUserName(request.userId).toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesUser && matchesSearch;
    });

    const sortedRequests = filteredRequests.sort((a, b) =>
        new Date(b.$createdAt).getTime() - new Date(a.$createdAt).getTime()
    );


    const totalPages = Math.ceil(sortedRequests.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredRequests.length);
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    const handleFilterChange = () => {
        setCurrentPage(1);
    };

    const pendingCount = requests.filter(r => r.statusByRrhh === 'pending').length;


    const RequestCard = ({
        request,
        onViewDetails
    }: {
        request: LeaveRequest;
        onViewDetails: (request: LeaveRequest) => void;
    }) => {
        const overallStatus = getOverallStatus(request);
        const rejectionReason = request.rejectionReasonByRrhh ||
            request.rejectionReasonBySupervisor ||
            request.rejectionReasonByManager;

        return (
            <Card className="hover:shadow-md transition-shadow overflow-hidden border-l-4 border-l-amber-400  dark:border-l-amber-500  hover:border-l-amber-500 dark:hover:border-l-amber-600">
                <CardHeader className="overflow-hidden">
                    <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 min-w-0 flex-1">
                            <CardTitle className="text-lg wrap-break-word overflow-wrap-anywhere">
                                {request.title}
                            </CardTitle>
                            <CardDescription className="space-y-1.5">
                                <div className="flex items-center gap-1.5">
                                    <Clock className="w-3 h-3 shrink-0" />
                                    <span className="text-xs">
                                        {formatDate(request.startDate)} - {formatDate(request.endDate)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                    <User className="w-3 h-3 text-muted-foreground shrink-0" />
                                    <span className="text-xs text-muted-foreground truncate">
                                        {getUserName(request.userId)}
                                    </span>
                                </div>
                            </CardDescription>
                        </div>
                        <StatusBadge status={overallStatus} />
                    </div>
                </CardHeader>

                <CardContent className="space-y-4 overflow-hidden">
                    {/* Info resumida */}
                    <div className="flex flex-wrap items-center gap-1 text-sm">
                        <Badge variant="outline" className="gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(request.totalDuration)}
                        </Badge>
                        <Badge variant={isFullDayLeave(request) ? "default" : "secondary"} className="gap-1">
                            {isFullDayLeave(request) ? <ClipboardList className="w-3 h-3" /> : <Watch className="w-3 h-3" />}
                            {isFullDayLeave(request) ? 'Todo el día' : 'Parcial'}
                        </Badge>
                    </div>

                    {/* Comentario truncado */}
                    {request.comment && (
                        <p className="text-sm text-muted-foreground line-clamp-2 wrap-break-word overflow-wrap-anywhere">
                            {request.comment}
                        </p>
                    )}

                    {/* <ApprovalSteps request={request} /> */}
                    {request.approvedById && overallStatus === 'approved' && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                            <span>Aprobado por: <span className="font-medium text-foreground">{getUserName(request.approvedById)}</span></span>
                        </div>
                    )}

                    {request.idRejectedByRrhh && overallStatus == 'rejected' && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                            <span>Rechazado por: <span className="font-medium text-foreground">{getUserName(request.idRejectedByRrhh)}</span></span>
                        </div>
                    )}


                    {overallStatus === 'rejected' && rejectionReason && (
                        <RejectionNotice reason={rejectionReason} />
                    )}

                    <Button
                        onClick={() => onViewDetails(request)}
                        className="w-full gap-2 cursor-pointer"
                        variant="outline"
                    >
                        <FileText className="w-4 h-4" />
                        Ver Detalles
                    </Button>
                </CardContent>
                <CardFooter className="flex gap-2">
                    {getOverallStatus(request) !== 'approved' && (
                        <>
                            <Button
                                onClick={() => handleApprove(request)}
                                className="flex-1 bg-green-600 hover:bg-green-700 cursor-pointer dark:text-white"
                            >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Aprobar
                            </Button>
                            <Button
                                onClick={() => handleReject(request)}
                                className="flex-1 cursor-pointer"
                            >
                                <XCircle className="w-4 h-4 mr-2" />
                                Rechazar
                            </Button>
                        </>
                    )}
                </CardFooter>

            </Card>
        );
    };


    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Permisos</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Administrar Permisos</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden">
                            <File className="w-5 h-5" />
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="py-6 pb-24">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-2xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                            Gestión de Permisos - Administrativo
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            Revisa y aprueba las solicitudes de permiso
                        </p>
                        {pendingCount > 0 && (
                            <Badge variant="secondary" className="mt-2">
                                {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Input
                            placeholder="Buscar por título, comentario o usuario..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                handleFilterChange();
                            }}
                            className="pl-9"
                        />
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>


                    <Select
                        value={userFilter}
                        onValueChange={(v) => {
                            setUserFilter(v);
                            handleFilterChange();
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-52">
                            <SelectValue placeholder="Filtrar por usuario" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los usuarios</SelectItem>
                            {usersWithRequests.map(([userId, userName]) => (
                                <SelectItem key={userId} value={userId}>
                                    {userName}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={statusFilter}
                        onValueChange={(v) => {
                            setStatusFilter(v as StatusFilter);
                            handleFilterChange();
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-48">
                            <SelectValue placeholder="Filtrar por estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="pending">Pendientes</SelectItem>
                            <SelectItem value="approved">Aprobados</SelectItem>
                            <SelectItem value="rejected">Rechazados</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Content */}
                {loading || loadingSubordinates ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : paginatedRequests.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-3">
                            {paginatedRequests.map((request) => (
                                <RequestCard
                                    key={request.$id}
                                    request={request}
                                    onViewDetails={handleViewDetails}
                                />
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            startIndex={startIndex}
                            endIndex={endIndex}
                            total={filteredRequests.length}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}

                {/* Details Dialog con Tabs */}
                <Dialog
                    open={detailsDialog.open}
                    onOpenChange={(open) => setDetailsDialog(prev => ({ ...prev, open }))}
                >
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto font-outfit">
                        <DialogHeader>
                            <DialogTitle>Detalles de la Solicitud</DialogTitle>
                            <DialogDescription>
                                Información completa del permiso solicitado
                            </DialogDescription>
                        </DialogHeader>

                        {detailsDialog.request && (
                            <Tabs defaultValue="details" className="w-full">
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="details">Detalles Generales</TabsTrigger>
                                    <TabsTrigger value="comment">Descripcion</TabsTrigger>
                                </TabsList>

                                <TabsContent value="details" className="space-y-4 mt-4">
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Título</label>
                                            <p className="text-base mt-1">{detailsDialog.request.title}</p>

                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Usuario</label>
                                                <p className="text-sm mt-1">{getUserName(detailsDialog.request.userId)}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Estado</label>
                                                <div className="mt-1">
                                                    <StatusBadge status={getOverallStatus(detailsDialog.request)} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Fecha Inicio</label>
                                                <p className="text-sm mt-1">
                                                    {isFullDayLeave(detailsDialog.request)
                                                        ? formatDate(detailsDialog.request.startDate)
                                                        : formatDateTime(detailsDialog.request.startDate)
                                                    }
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Fecha Fin</label>
                                                <p className="text-sm mt-1">
                                                    {isFullDayLeave(detailsDialog.request)
                                                        ? formatDate(detailsDialog.request.endDate)
                                                        : formatDateTime(detailsDialog.request.endDate)
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Duración Total</label>
                                                <p className="text-sm mt-1">{formatDuration(detailsDialog.request.totalDuration)}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Tipo</label>
                                                <div className="mt-1">
                                                    <Badge variant={isFullDayLeave(detailsDialog.request) ? "default" : "secondary"}>
                                                        {isFullDayLeave(detailsDialog.request) ? 'Todo el día' : 'Parcial'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Solicitado el</label>
                                            <p className="text-sm mt-1">{formatDateTime(detailsDialog.request.$createdAt)}</p>
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Estado de Aprobaciones</label>
                                            {/* <ApprovalSteps request={detailsDialog.request} /> */}

                                            {detailsDialog.request.approvedById && getOverallStatus(detailsDialog.request) === 'approved' && (
                                                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2 ">
                                                    <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                                                    <span>Aprobado por: <span className="font-medium  text-foreground">{getUserName(detailsDialog.request.approvedById)}</span></span>
                                                </div>
                                            )}

                                            {detailsDialog.request.idRejectedByRrhh && getOverallStatus(detailsDialog.request) === 'rejected' && (
                                                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2">
                                                    <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                                                    <span>Rechazado por: <span className="font-medium text-foreground">{getUserName(detailsDialog.request.idRejectedByRrhh)}</span></span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent value="comment" className="mt-4">
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Descripción de la solicitud
                                        </label>
                                        {detailsDialog.request.comment ? (
                                            <div className="bg-muted/30 rounded-md p-4">
                                                <p className="text-sm whitespace-pre-wrap wrap-break-word">
                                                    {detailsDialog.request.comment}
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground italic">
                                                No se proporcionó una descripción
                                            </p>
                                        )}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        )}

                        <DialogFooter className="flex gap-2">
                            {detailsDialog.request && getOverallStatus(detailsDialog.request) !== 'approved' && (
                                <>
                                    <Button
                                        onClick={() => detailsDialog.request && handleReject(detailsDialog.request)}
                                        variant="destructive"
                                        className="cursor-pointer"
                                    >
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Rechazar
                                    </Button>
                                    <Button
                                        onClick={() => detailsDialog.request && handleApprove(detailsDialog.request)}
                                        className="bg-green-600 hover:bg-green-700 cursor-pointer"
                                    >
                                        <CheckCircle2 className="w-4 h-4 mr-2" />
                                        Aprobar
                                    </Button>
                                </>
                            )}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Approval/Rejection Dialog */}
                <Dialog
                    open={approvalDialog.open}
                    onOpenChange={(open) => {
                        if (!processing) {
                            setApprovalDialog(prev => ({ ...prev, open }));
                        }
                    }}
                >
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {approvalDialog.action === 'approve'
                                    ? '¿Aprobar solicitud?'
                                    : '¿Rechazar solicitud?'}
                            </DialogTitle>
                            <DialogDescription>
                                {approvalDialog.action === 'approve'
                                    ? '¿Estás seguro de que deseas aprobar esta solicitud? Esta acción no se puede deshacer.'
                                    : 'El usuario será notificado del rechazo de su solicitud.'}
                            </DialogDescription>
                        </DialogHeader>

                        {approvalDialog.action === 'reject' && (
                            <div className="grid gap-2 py-4">
                                <label className="text-sm font-medium">
                                    Motivo del rechazo <span className="text-red-500">*</span>
                                </label>
                                <Textarea
                                    placeholder="Explica por qué se rechaza la solicitud..."
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={3}
                                    disabled={processing}
                                />
                            </div>
                        )}

                        <DialogFooter>
                            <Button
                                variant="outline"
                                className='cursor-pointer'
                                onClick={() => {
                                    setApprovalDialog({ open: false, request: null, action: null });
                                    setRejectionReason('');
                                }}
                                disabled={processing}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={confirmApproval}
                                disabled={processing ||
                                    (approvalDialog.action === 'reject' && !rejectionReason.trim())}
                                variant={approvalDialog.action === 'reject' ? 'destructive' : 'default'}
                                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700 cursor-pointer' : ''}
                            >
                                {processing ? 'Procesando...' : 'Confirmar'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
};

export default RRHHLeaveRequestsView;